import React, { useState, useEffect, useCallback } from 'react';
import Table from '../../../components/Table/Table';
import SearchBar from '../../../components/SearchBar/SearchBar';
import Button from '../../../components/Button/Button';
import Pagination from '../../../components/Pagination/Pagination';
import { useAdminOwners } from '../../../hooks/useAdminOwners';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import type { AdminOwnerDto } from '../../../services/api';
import GrantSubscriptionModal from './GrantSubscriptionModal';
import AccountStatusConfirmModal from '../AccountStatus/AccountStatusConfirmModal';
import BusinessBookingsModal from './BusinessBookingsModal';
import SalonStatusModal from '../Support/SalonStatusModal';
import styles from './OwnersPage.module.css';

const OwnersPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebouncedValue(searchTerm, 300);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    // AB#2045 — owner selected for the "Grant founding access" modal.
    const [grantOwner, setGrantOwner] = useState<AdminOwnerDto | null>(null);
    // P0-7 (AB#2047) — owner selected for the read-only "Recent bookings" panel.
    const [bookingsOwner, setBookingsOwner] = useState<AdminOwnerDto | null>(null);
    // AB#2112 — the owner account whose state the admin is about to change (confirmed first).
    const [statusTarget, setStatusTarget] = useState<{ owner: AdminOwnerDto; nextActive: boolean } | null>(null);
    // Batch C (AB#2195) — the owner whose salon the admin suspends / reinstates.
    const [salonStatusOwner, setSalonStatusOwner] = useState<AdminOwnerDto | null>(null);

    const { loading, error, owners, totalCount, fetchOwners, toggleStatus } = useAdminOwners();
    const { t } = useLanguage();

    const loadOwners = useCallback(async () => {
        try {
            await fetchOwners({
                search: debouncedSearch || undefined,
                page: currentPage,
                pageSize
            });
        } catch {
            // handled by hook
        }
    }, [fetchOwners, debouncedSearch, currentPage, pageSize]);

    useEffect(() => { loadOwners(); }, [loadOwners]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    }, []);

    const tableHeaders = ['Name', 'Email', 'Business Name', 'Status', 'Actions'];
    const tableData = (owners ?? []).map((o, i) => {
        const status = o.status ?? '';
        const isActive = status.toLowerCase() === 'active';
        const hasSuspendedSalon = (o.businesses ?? []).some(b => b.status === 'Suspended');
        return {
            originalIndex: i,
            'Name': o.fullName,
            'Email': o.email,
            'Business Name': o.businessName,
            hasSuspendedSalon,
            'Status': (
                <span className={`${styles.statusBadge} ${isActive ? styles.statusActive : styles.statusInactive}`}>
                    {status || '—'}
                </span>
            ),
            'Actions': (
                <div className={styles.rowActions}>
                    <Button
                        label={t(isActive ? 'superadmin.accountStatus.action.deactivate' : 'superadmin.accountStatus.action.activate')}
                        onClick={() => setStatusTarget({ owner: o, nextActive: !isActive })}
                        variant="secondary"
                        size="small"
                    />
                    {(o.businesses?.length ?? 0) > 0 && (
                        <Button
                            label={t('superadmin.owners.grant.action.open')}
                            onClick={() => setGrantOwner(o)}
                            variant="primary"
                            size="small"
                        />
                    )}
                    {(o.businesses?.length ?? 0) > 0 && (
                        <Button
                            label={t('superadmin.salonStatus.action.open')}
                            onClick={() => setSalonStatusOwner(o)}
                            variant="secondary"
                            size="small"
                        />
                    )}
                    {(o.businesses?.length ?? 0) > 0 && (
                        <Button
                            label={t('superadmin.bookings.action.open')}
                            onClick={() => setBookingsOwner(o)}
                            variant="secondary"
                            size="small"
                        />
                    )}
                </div>
            )
        };
    });

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Salon Owners</h1>
                <p className={styles.subtitle}>Manage salon owners on the platform</p>
            </div>

            <div className={styles.controlsHeaderContainer}>
                <div className={styles.searchContainer}>
                    <SearchBar
                        placeholder="Search by name or email..."
                        onChange={handleSearchChange}
                        value={searchTerm}
                        showClearButton={true}
                    />
                </div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                    <Button label="Retry" onClick={loadOwners} size="small" />
                </div>
            )}

            <div className={styles.tableWrapper}>
                <Table
                    headers={tableHeaders}
                    data={tableData}
                    loading={loading}
                    nonSortableColumns={['Actions', 'Status']}
                    customRenderers={{
                        // Batch C (AB#2195) — flag an owner whose salon is suspended.
                        'Business Name': (v, _i, row) => row.hasSuspendedSalon ? (
                            <span className={styles.businessCell}>
                                <bdi>{v}</bdi>
                                <span className={`${styles.statusBadge} ${styles.statusInactive}`}>
                                    {t('superadmin.salonStatus.status.suspended')}
                                </span>
                            </span>
                        ) : v,
                        'Status': (v) => v,
                        'Actions': (v) => v,
                    }}
                />
            </div>

            {!loading && totalPages > 1 && (
                <div className={styles.paginationWrapper}>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
            )}

            {(owners?.length ?? 0) === 0 && !loading && !error && (
                <div className={styles.emptyState}>
                    <h3>No owners found</h3>
                    <p>Try adjusting your search.</p>
                </div>
            )}

            {statusTarget && (
                <AccountStatusConfirmModal
                    open
                    kind="owner"
                    target={{ id: statusTarget.owner.id, fullName: statusTarget.owner.fullName, email: statusTarget.owner.email }}
                    nextActive={statusTarget.nextActive}
                    onConfirm={() => toggleStatus(statusTarget.owner.id, statusTarget.nextActive)}
                    onClose={() => setStatusTarget(null)}
                    onChanged={loadOwners}
                />
            )}

            {grantOwner && (
                <GrantSubscriptionModal
                    open={grantOwner != null}
                    owner={grantOwner}
                    onClose={() => setGrantOwner(null)}
                    onGranted={loadOwners}
                />
            )}

            {salonStatusOwner && (
                <SalonStatusModal
                    ownerName={salonStatusOwner.fullName}
                    businesses={salonStatusOwner.businesses ?? []}
                    onClose={() => setSalonStatusOwner(null)}
                    onChanged={loadOwners}
                />
            )}

            {bookingsOwner && (
                <BusinessBookingsModal
                    open={bookingsOwner != null}
                    owner={bookingsOwner}
                    onClose={() => setBookingsOwner(null)}
                />
            )}
        </div>
    );
};

export default OwnersPage;
